// Admin → Email tab.
//
// Shows the current SMTP configuration (password masked), lets the admin run
// a connection test (no mail sent), and lets them send a one-off test email
// to verify deliverability. Backed by /api/admin/email/* endpoints.
//
// New signups: this tab is also the place to confirm verification emails are
// being delivered correctly. The "Send Test" button defaults to the admin's
// own email so they can pop their inbox and confirm — but they can target
// any address (e.g. a recent signup's email).

import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, ShieldCheck, AlertTriangle, CheckCircle2, Loader2, Send, Zap, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { host: "", port: "", username: "", from_address: "", app_base_url: "", password: "" };

export default function AdminEmailTab() {
  const [cfg, setCfg] = useState(null);
  const [cfgLoading, setCfgLoading] = useState(false);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saveBusy, setSaveBusy] = useState(false);

  const [testConnBusy, setTestConnBusy] = useState(false);
  const [testConnResult, setTestConnResult] = useState(null);   // { ok, message }

  const [sendBusy, setSendBusy] = useState(false);
  const [sendResult, setSendResult] = useState(null);           // { ok, message, to }
  const [recipient, setRecipient] = useState("");

  const loadConfig = async () => {
    setCfgLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/email/config`);
      setCfg(data);
    } catch (e) {
      toast.error("Failed to load SMTP config");
    } finally {
      setCfgLoading(false);
    }
  };

  useEffect(() => { loadConfig(); }, []);

  const handleEditOpen = () => {
    setForm({
      host: cfg?.host || "",
      port: cfg?.port ?? "",
      username: cfg?.username || "",
      from_address: cfg?.from_address || "",
      app_base_url: cfg?.app_base_url || "",
      password: "",
    });
    setEditing(true);
  };

  const handleEditCancel = () => {
    setEditing(false);
    setForm(EMPTY_FORM);
  };

  const handleFormChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaveBusy(true);
    try {
      const payload = {
        host: form.host,
        port: Number(form.port),
        username: form.username,
        from_address: form.from_address,
        app_base_url: form.app_base_url,
      };
      if (form.password.trim()) {
        payload.password = form.password;
      }
      await axios.put(`${API}/admin/email/config`, payload);
      toast.success("SMTP configuration saved");
      setEditing(false);
      setForm(EMPTY_FORM);
      await loadConfig();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Failed to save SMTP config";
      toast.error(msg);
    } finally {
      setSaveBusy(false);
    }
  };

  const handleTestConnection = async () => {
    setTestConnBusy(true);
    setTestConnResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/email/test-connection`);
      setTestConnResult(data);
      if (data.ok) toast.success("SMTP connection OK");
      else toast.error("SMTP connection failed");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Unknown error";
      setTestConnResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setTestConnBusy(false);
    }
  };

  const handleSendTest = async () => {
    setSendBusy(true);
    setSendResult(null);
    try {
      const payload = recipient.trim() ? { to: recipient.trim() } : {};
      const { data } = await axios.post(`${API}/admin/email/send-test`, payload);
      setSendResult(data);
      if (data.ok) toast.success(`Test email sent to ${data.to}`);
      else toast.error(data.message || "Send failed");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Unknown error";
      setSendResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setSendBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-email-tab">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-4 w-4" /> SMTP Configuration
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Outbound mail server used for account verification, password resets,
                and feedback messages. Configured via the backend <code className="text-[10px] bg-muted px-1 rounded">.env</code>.
              </p>
            </div>
            {!cfgLoading && cfg && !editing && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditOpen}
                data-testid="email-config-edit-btn"
                className="shrink-0"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {cfgLoading || !cfg ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="email-config-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading SMTP config…
            </div>
          ) : editing ? (
            <div className="space-y-4" data-testid="email-config-edit-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="smtp-host" className="text-xs">Host</Label>
                  <Input
                    id="smtp-host"
                    type="text"
                    value={form.host}
                    onChange={handleFormChange("host")}
                    placeholder="smtp.example.com"
                    data-testid="smtp-host-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="smtp-port" className="text-xs">Port</Label>
                  <Input
                    id="smtp-port"
                    type="number"
                    value={form.port}
                    onChange={handleFormChange("port")}
                    placeholder="587"
                    data-testid="smtp-port-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="smtp-username" className="text-xs">Username</Label>
                  <Input
                    id="smtp-username"
                    type="email"
                    value={form.username}
                    onChange={handleFormChange("username")}
                    placeholder="user@example.com"
                    data-testid="smtp-username-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="smtp-from-address" className="text-xs">From Address</Label>
                  <Input
                    id="smtp-from-address"
                    type="email"
                    value={form.from_address}
                    onChange={handleFormChange("from_address")}
                    placeholder="noreply@example.com"
                    data-testid="smtp-from-address-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="smtp-password" className="text-xs">Password</Label>
                  <Input
                    id="smtp-password"
                    type="password"
                    value={form.password}
                    onChange={handleFormChange("password")}
                    placeholder="Leave blank to keep current password"
                    data-testid="smtp-password-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="smtp-app-base-url" className="text-xs">App Base URL</Label>
                  <Input
                    id="smtp-app-base-url"
                    type="text"
                    value={form.app_base_url}
                    onChange={handleFormChange("app_base_url")}
                    placeholder="https://app.example.com"
                    data-testid="smtp-app-base-url-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saveBusy}
                  data-testid="smtp-save-btn"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {saveBusy
                    ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    : <Check className="h-4 w-4 mr-1.5" />
                  }
                  Save Changes
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditCancel}
                  disabled={saveBusy}
                  data-testid="smtp-cancel-btn"
                >
                  <X className="h-4 w-4 mr-1.5" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <Row k="Host" v={cfg.host || "—"} />
              <Row k="Port" v={cfg.port} mono />
              <Row k="Username" v={cfg.username || "—"} mono />
              <Row k="From address" v={cfg.from_address || "—"} mono />
              <Row
                k="Password"
                v={cfg.password_set ? `set (${cfg.password_length} chars)` : "not set"}
                badge={cfg.password_set ? "ok" : "warn"}
              />
              <Row
                k="Status"
                v={cfg.configured ? "Configured" : "Incomplete"}
                badge={cfg.configured ? "ok" : "warn"}
              />
              <Row k="App base URL" v={cfg.app_base_url || "—"} mono span2 />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4" /> Connection Test
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Establishes an SMTP session and authenticates against the configured host.
            No email is sent. Use this to verify host/port and credentials.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            onClick={handleTestConnection}
            disabled={testConnBusy || !cfg?.configured}
            data-testid="email-test-connection-btn"
            size="sm"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {testConnBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
            Test SMTP Connection
          </Button>
          {testConnResult && <ResultBox res={testConnResult} testid="email-test-connection-result" />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Send className="h-4 w-4" /> Send Test Email
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Sends a short test email so you can confirm deliverability end-to-end.
            Leave blank to send to your own admin address.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2 space-y-1">
              <Label htmlFor="email-test-recipient" className="text-xs">Recipient (optional)</Label>
              <Input
                id="email-test-recipient"
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="Defaults to your admin email"
                data-testid="email-test-recipient-input"
              />
            </div>
            <Button
              onClick={handleSendTest}
              disabled={sendBusy || !cfg?.configured}
              data-testid="email-send-test-btn"
              size="sm"
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {sendBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Send Test
            </Button>
          </div>
          {sendResult && <ResultBox res={sendResult} testid="email-send-test-result" />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4" /> New Signups
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            When a new user registers, ZAMBA emails them a verification link via
            this SMTP server. If signups are failing to receive their email, run the
            test above first.
          </p>
        </CardHeader>
        <CardContent className="text-sm text-foreground">
          New signup verifications are routed through{" "}
          <span className="font-mono text-foreground">
            {cfg?.from_address || "—"}
          </span>
          {" "}via{" "}
          <span className="font-mono text-foreground">
            {cfg?.host || "—"}:{cfg?.port || "—"}
          </span>
          .
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ k, v, mono = false, badge, span2 = false }) {
  return (
    <div className={`flex items-baseline gap-3 py-1 border-b border-border ${span2 ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-wide text-muted-foreground w-28 shrink-0">{k}</span>
      <span className={`text-sm text-foreground ${mono ? "font-mono" : ""} flex-1 break-all`}>
        {String(v)}
      </span>
      {badge === "ok" && <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />}
      {badge === "warn" && <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />}
    </div>
  );
}

function ResultBox({ res, testid }) {
  const ok = !!res.ok;
  return (
    <div
      className={`flex items-start gap-2 text-sm rounded p-3 border ${
        ok
          ? "bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-300"
          : "bg-rose-50 border-rose-200 text-rose-900 dark:bg-rose-900/20 dark:border-rose-800 dark:text-rose-300"
      }`}
      data-testid={testid}
    >
      {ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
      <div className="flex-1">
        <div className="font-medium">{ok ? "Success" : "Failed"}</div>
        <div className="text-xs mt-0.5 whitespace-pre-wrap">{res.message}</div>
      </div>
    </div>
  );
}
